import { useCallback, useEffect, useEffectEvent, useRef } from 'react';

const VISIBILITY_CHECK_INTERVAL = 100;

const isElementVisible = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;

  const topElement = document.elementFromPoint(centerX, centerY);
  return (
    !!topElement && (element.contains(topElement) || element === topElement)
  );
};

export const useWaitForElementVisible = (
  elementRef: React.RefObject<HTMLElement | null>,
  onVisible: () => void,
  enabled: boolean
) => {
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onVisibleEvent = useEffectEvent(onVisible);

  const clearCheck = useCallback(() => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      clearCheck();
      return () => {};
    }

    const checkVisibility = () => {
      if (elementRef.current && isElementVisible(elementRef.current)) {
        clearCheck();
        onVisibleEvent();
      }
    };

    // Check immediately
    checkVisibility();

    // If still not visible, keep checking
    if (!checkIntervalRef.current) {
      checkIntervalRef.current = setInterval(
        checkVisibility,
        VISIBILITY_CHECK_INTERVAL
      );
    }

    return clearCheck;
  }, [enabled, elementRef, clearCheck, onVisibleEvent]);
};
